"use client"

import Image from "next/image"
import { ArrowUpRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { getDirectusImageUrl, type DirectusProduct } from "@/lib/directus"

interface InlineProductCardProps {
  product: DirectusProduct
  /** 1-indexed rank used for the optional bestseller signal ("↗ #1"). */
  rank?: number
  /** Show the bestseller signal next to the category chip. */
  showRank?: boolean
  /** Show the raw Amazon price. Defaults OFF — site policy hides price on rec
   *  cards and routes users to Amazon via the CTA. See ProductCard.showPrice. */
  showPrice?: boolean
  onClick?: (product: DirectusProduct) => void
  /** Affiliate surface stamped into `data-affiliate-placement` so a crawl of the
   *  rendered HTML reports the same bucket the click event already sends. */
  placement?: string
  className?: string
}

export function InlineProductCard({
  product,
  rank,
  showRank = false,
  showPrice = false,
  onClick,
  placement,
  className,
}: InlineProductCardProps) {
  const formattedPrice = showPrice ? formatPrice(product.price) : null
  const Wrapper = product.affiliate_url ? "a" : "div"
  const wrapperProps = product.affiliate_url
    ? {
        href: product.affiliate_url,
        target: "_blank" as const,
        rel: "noopener noreferrer sponsored nofollow",
        "data-affiliate": "true",
        ...(placement ? { "data-affiliate-placement": placement } : {}),
        onClick: () => onClick?.(product),
      }
    : {}

  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        "group relative flex items-stretch gap-2.5 rounded-xl border border-gray-200 bg-white p-2.5 transition-colors sm:gap-4 sm:p-3",
        product.affiliate_url && "hover:border-green-300 hover:bg-green-50/30",
        className,
      )}
    >
      {/* Image — shrinks on mobile to give content more room */}
      <div className="flex h-[68px] w-[68px] flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-gray-50 sm:h-[88px] sm:w-[88px]">
        <Image
          src={getDirectusImageUrl(product.image, 176, 176)}
          alt={product.name}
          width={176}
          height={176}
          loading="lazy"
          className="h-full w-full object-contain p-1.5"
        />
      </div>

      {/* Content — eyebrow + title */}
      <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
        <div className="min-w-0">
          <div className="mb-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
            <span className="inline-flex max-w-full items-center truncate rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-green-700">
              {shortCategory(product.category)}
            </span>
            {showRank && rank !== undefined && (
              <span className="inline-flex items-center gap-0.5 whitespace-nowrap text-[11px] font-semibold text-green-700">
                <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
                <span>#{rank}</span>
              </span>
            )}
          </div>
          <h4
            className="line-clamp-2 text-[13px] font-semibold leading-tight text-gray-900 group-hover:text-green-700 sm:text-[14px]"
            title={product.name}
          >
            {product.name}
          </h4>
        </div>
      </div>

      {/* Right rail — vertical hairline + price + Buy button */}
      <div className="flex flex-shrink-0 items-stretch gap-3 border-l border-gray-100 pl-2 -ml-px sm:pl-3">
        <div className={cn("flex min-w-[58px] flex-col items-end py-0.5 sm:min-w-[68px]", formattedPrice ? "justify-between" : "justify-center")}>
          {formattedPrice && (
            <span className="text-[14px] font-extrabold tabular-nums tracking-tight text-gray-900 sm:text-[15px]">
              {formattedPrice}
            </span>
          )}
          {product.affiliate_url ? (
            <span className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-md bg-green-700 px-2 py-1 text-[11px] font-bold text-white shadow-xs transition-colors group-hover:bg-green-800 sm:gap-1 sm:px-2.5 sm:text-[11.5px]">
              Buy
              <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
            </span>
          ) : null}
        </div>
      </div>
    </Wrapper>
  )
}

function formatPrice(price: number | string | null | undefined): string | null {
  if (price === null || price === undefined || price === "") return null
  const value = typeof price === "string" ? Number.parseFloat(price) : price
  if (!Number.isFinite(value)) return null
  return `$${value.toFixed(2)}`
}

// Shorten verbose Directus categories so they fit the 1-line chip.
const CATEGORY_SHORTHAND: Record<string, string> = {
  "Fertilizers & Soil Health": "Fertilizer",
  "Weed Control": "Weed Control",
  "Herbicides & Weed Control": "Herbicide",
  "Lawn Mowers & Equipment": "Mower",
  "Lawn Mowers & Maintenance Tools": "Tools",
  "Soil & Lab Tests": "Soil Test",
  "Watering & Irrigation": "Watering",
  "Seeds & Patches": "Seed",
  "Pest Control": "Pest Control",
  "Lawn Care Tools": "Tools",
}

function shortCategory(category: string): string {
  return CATEGORY_SHORTHAND[category] ?? category
}
